import { Alert, Box, Button, CircularProgress, Divider, Grid, Paper, Skeleton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ControlledDatePicker from 'src/shared/components/form/controlledDatePicker';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { useFormContext } from 'react-hook-form';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { useInvoiceContext } from 'src/shared/contexts/invoiceContext';
import ControlledTextEditor from 'src/shared/components/form/controlledTextEditor';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { useEffect, useState } from 'react';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { getInvoiceTemplateById } from 'src/shared/services/invoiceService';
import { ControlledCheckboxNew } from 'src/shared/components/form/controlledCheckboxNew';
import { InvoiceSendingTypeIntegerEnum } from 'src/shared/models/enums/invoiceSendingTypeInteger.enum';
import { CreateMemberInvoicesPreviewRequest } from '../models/createMemberInvoicesPreviewRequest';
import { previewInvoiceDownloader } from './previewInvoiceDownloader';
import { InvoiceTemplate } from '../models/invoiceTemplate';
import { CreateMemberInvoiceSkeleton } from './createMemberInvoiceSkeleton';
import { SetSendingDate } from './setSendingDate';

type CreateMemberInvoiceProps = {
  setLoading: (pdfLoader: boolean) => void;
  tempId: number;
  isSuperAdmin: boolean;
};

export const CreateMemberInvoice = (props: CreateMemberInvoiceProps) => {
  const { setLoading, tempId, isSuperAdmin } = props;
  const { t } = useTranslation();
  const { invoiceCountStats } = useInvoiceContext();
  const isMounted = useIsMounted();

  const [loadingContent, setLoadingContent] = useState<boolean>(false);
  const [data, setData] = useState<InvoiceTemplate | null>(null);
  const { setValue, getValues, watch } = useFormContext();

  const approvalRequired = watch('approvalRequired');

  const dataToPdf: CreateMemberInvoicesPreviewRequest = {
    templateId: tempId,
    payDate: DateHelper.getFSDate(getValues('payDate')),
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoadingContent(true);
      try {
        const res = await getInvoiceTemplateById(tempId, isSuperAdmin ? true : undefined);
        if (isMounted()) {
          setData(res.data);
        }
      } catch (e) {
        if (isMounted()) ConsoleHelper.log(e);
      } finally {
        if (isMounted()) setLoadingContent(false);
      }
    };

    fetchData();
  }, [isMounted, isSuperAdmin, tempId]);

  useEffect(() => {
    if (data) {
      const sendingContent = data.sendings?.find((sending) => sending.sendingType === InvoiceSendingTypeIntegerEnum.Main);
      if (sendingContent) {
        setValue('defaultHeading', sendingContent.defaultHeading);
        setValue('defaultText', sendingContent.defaultText);
        setValue('emailHeading', sendingContent.emailHeading);
        setValue('emailText', sendingContent.emailText);
      }
    }
  }, [data, setValue]);

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={5}>
        <Grid item xs={12} md={7.5}>
          <Typography variant='h6'>{t('invoice:createMemberInvoice.invoiceContent')}</Typography>
          <Paper
            sx={{
              border: '2px solid, lightGrey',
              background: '#F5F5F5',
              boxShadow: 4,
            }}
          >
            <Grid container px={2} mt={1}>
              <Grid item xs={12} sm={8}>
                <CreateMemberInvoiceSkeleton />
              </Grid>
              <Grid item xs={12} sm={4} mt={3}>
                <Grid item xs mb={1}>
                  <ControlledDatePicker label={t('invoice:InvoiceDate')} name={t('invoice:createMemberInvoice.missionDate')} />
                </Grid>
                <Grid item xs>
                  <ControlledDatePicker label={t('invoice:createMemberInvoice.lastPaymentDate')} name='payDate' />
                </Grid>
              </Grid>
              <Grid item xs mt={3}>
                <ControlledTextField disabled name='defaultHeading' label={t('invoice:createMemberInvoice.heading')} />
              </Grid>
              <Grid container mt={2} mb={2}>
                <Grid item xs={9}>
                  <Typography variant='body2' color='textSecondary'>
                    {t('invoice:createMemberInvoice.memberNumber')}
                  </Typography>
                  <Skeleton variant='text' animation='wave' sx={{ height: '20px', width: '90%' }} />
                </Grid>
                <Grid item xs={3}>
                  <Typography variant='body2' color='textSecondary'>
                    {t('invoice:createMemberInvoice.fee')}
                  </Typography>
                  <Skeleton variant='text' animation='wave' sx={{ height: '20px', width: '95%' }} />
                </Grid>
              </Grid>
              {loadingContent ? (
                <Grid container justifyContent='center' mt={5} mb={2}>
                  <CircularProgress size={35} />
                </Grid>
              ) : (
                <Grid item xs={12} mb={2}>
                  <ControlledTextEditor disabled rows={30} name='defaultText' />
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4.5}>
          <Typography variant='h6'>{t('invoice:createMemberInvoice.nameTheInvoice')}</Typography>

          <Grid item xs={12} mt={1} mb={2}>
            <ControlledTextField name={t('invoice:createMemberInvoice.missionName')} label={t('invoice:Name')} />
            <Typography variant='body2' color='textSecondary' sx={{ fontSize: '12px', ml: 0.5, mt: 0.5, fontStyle: 'italic' }}>
              {t('invoice:createMemberInvoice.invoiceIsSavedWithTheAboveName')}
            </Typography>
          </Grid>
          <Typography variant='h6'>{t('invoice:createMemberInvoice.preview')}</Typography>
          <Grid item xs={12} mt={1}>
            <Alert severity='info'>
              {t('invoice:createMemberInvoice.createInvoiceTemplateWarningText', { count: invoiceCountStats?.totalCount })}
              <Grid item xs>
                <Button
                  variant='outlined'
                  sx={{
                    mt: 2,
                  }}
                  onClick={() => previewInvoiceDownloader({ dataToPdf, loadingCallback: setLoading })}
                >
                  {t('invoice:createMemberInvoice.previewInvoice')}
                </Button>
              </Grid>
            </Alert>
          </Grid>

          {isSuperAdmin && (
            <>
              <Grid item xs={12} mt={1}>
                <ControlledCheckboxNew name='runAsTaskWithTaskService' label={t('invoice:runAsTask')} />
              </Grid>
              <Grid item xs={12} mt={1} justifyContent='center'>
                <Divider sx={{ mt: 2 }} />
                <ControlledCheckboxNew
                  name='approvalRequired'
                  customizedLabel={
                    <>
                      <Typography variant='body2' mt={0.5}>
                        {t('invoice:createMemberInvoice.mustBeApprovedByCustomer')}
                      </Typography>
                    </>
                  }
                />
              </Grid>
              {data?.sendingApprovalEmails && (
                <Typography ml={4} variant='body2' color='textSecondary'>
                  {t('invoice:invoiceMissionPage.approvalNotice', { email: data.sendingApprovalEmails })}
                </Typography>
              )}
              <Grid item xs={9} ml={4} mt={3} justifyContent='center'>
                <SetSendingDate disabled={!approvalRequired} />
              </Grid>
            </>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};
